import { actions } from "@app/utils";
import { createApiClient } from "@app/utils/apiClient";

export const adminClient = createApiClient(actions["exd-storyteller-ai/admin"]);

export const getUsers = async (options = {}) => {
  const response = await adminClient.get("/users", {}, options);

  if (!response.ok) {
    throw new Error("Failed to fetch users", { cause: response });
  }

  return response.data.users ?? [];
};

export const getAdminStats = async (filters = {}, options = {}) => {
  const response = await adminClient.get("/stats", filters, options);

  if (!response.ok) {
    throw new Error("Failed to fetch admin stats", { cause: response });
  }

  return response.data ?? {};
};

export const getVideosReport = async (filters = {}, options = {}) => {
  const response = await adminClient.get("/videos-report", filters, options);

  if (!response.ok) {
    throw new Error("Failed to fetch videos report", { cause: response });
  }

  return response.data?.videos ?? [];
};

export const getAdminVideos = async (filters = {}, options = {}) => {
  const response = await adminClient.get("/videos", filters, options);

  if (!response.ok) {
    throw new Error("Failed to fetch admin videos", { cause: response });
  }

  return response.data?.videos ?? [];
};

export const updateAdminVideoComment = async (id, comment, options = {}) => {
  if (!id) {
    throw new Error("Video ID is required to update comment");
  }

  const response = await adminClient.patch(
    `/videos/${encodeURIComponent(id)}`,
    { comment },
    {},
    options,
  );

  if (!response.ok) {
    throw new Error("Failed to update project comment", { cause: response });
  }

  return response.data?.video ?? null;
};

export const getAdminVideoJobs = async (id, options = {}) => {
  if (!id) {
    throw new Error("Video ID is required to fetch jobs");
  }

  const response = await adminClient.get(
    `/videos/${encodeURIComponent(id)}/jobs`,
    {},
    options,
  );

  if (!response.ok) {
    throw new Error("Failed to fetch project jobs", { cause: response });
  }

  return response.data?.jobs ?? [];
};

export const getAdminJobCurl = async (jobId, options = {}) => {
  if (!jobId) {
    throw new Error("Job ID is required to generate cURL");
  }

  const response = await adminClient.post(
    `/jobs/${encodeURIComponent(jobId)}/curl`,
    {},
    options,
  );

  if (!response.ok) {
    const message =
      response.data?.error?.body?.message ||
      response.data?.message ||
      "Failed to generate cURL for job";
    throw new Error(message, { cause: response });
  }

  return response.data?.curl ?? "";
};

const postJobAction = async (jobId, action, fallbackMessage, options) => {
  if (!jobId) {
    throw new Error("Job ID is required");
  }

  const response = await adminClient.post(
    `/jobs/${encodeURIComponent(jobId)}/${action}`,
    {},
    options,
  );

  if (!response.ok) {
    const message =
      response.data?.error?.body?.message ||
      response.data?.message ||
      fallbackMessage;
    throw new Error(message, { cause: response });
  }

  return response.data;
};

export const retryAdminJobUpload = (jobId, options = {}) =>
  postJobAction(jobId, "retry-upload", "Failed to retry upload", options);

export const rerenderAdminJob = (jobId, options = {}) =>
  postJobAction(jobId, "rerender", "Failed to re-render job", options);

export const getIntegrationsStatus = async ({
  complex = false,
  ...options
} = {}) => {
  const response = await adminClient.get("/integrations", { complex }, options);

  if (!response.ok) {
    throw new Error("Failed to fetch integrations status", { cause: response });
  }

  return response.data ?? { checkedAt: null, integrations: [] };
};

export const refreshIntegrationsStatus = async ({
  complex = false,
  ...options
} = {}) => {
  const response = await adminClient.post(
    "/integrations/refresh",
    {},
    { params: { complex }, ...options },
  );

  if (!response.ok) {
    throw new Error("Failed to refresh integrations status", {
      cause: response,
    });
  }

  return response.data ?? { checkedAt: null, integrations: [] };
};

export const addUserRole = async (userID, role, options = {}) => {
  const response = await adminClient.patch(
    `/users/${encodeURIComponent(userID)}/roles`,
    { role },
    {},
    options,
  );

  if (!response.ok) {
    throw new Error("Failed to add user role", { cause: response });
  }

  return response.data?.user ?? null;
};
